"use client";

import Image from "next/image";
import Link from "next/link";
import { MessageCircle, ArrowRight } from "lucide-react";
import { APP_LOGO_PATH, WHATSAPP_CONSULTOR_GENERAL } from "@/lib/constants";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const CURRENT_YEAR = new Date().getFullYear();

const WA_CTA_MSG = encodeURIComponent(
  "Olá! Vim pela landing page e gostaria de saber mais sobre o NoWey CRM."
);

const WA_HREF = `https://wa.me/${WHATSAPP_CONSULTOR_GENERAL}?text=${WA_CTA_MSG}`;

const NAV_LINKS = [
  { label: "Política de Privacidade", href: "/privacidade" },
  { label: "Termos de Uso", href: "/termos" },
  { label: "Contato", href: WA_HREF, external: true },
] as const;

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------
export default function Footer() {
  return (
    <footer
      className="relative z-10 bg-zinc-950 border-t border-white/5"
      aria-label="Rodapé"
    >
      {/* ── CTA discreto ── */}
      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-zinc-400 text-center sm:text-left">
            Pronto para organizar sua rede?
          </p>
          <a
            href={WA_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-primary transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
            aria-label="Fale com a equipe NoWey pelo WhatsApp"
          >
            Fale com a gente
            <ArrowRight
              className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
              aria-hidden
            />
          </a>
        </div>
      </div>

      {/* ── Main footer ── */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">

          {/* Logo + tagline */}
          <div className="flex flex-col items-center sm:items-start gap-2">
            <div className="flex items-center gap-2.5">
              <div className="relative w-7 h-7 shrink-0">
                <Image
                  src={APP_LOGO_PATH}
                  alt="NoWey"
                  fill
                  className="object-contain"
                  sizes="28px"
                />
              </div>
              <span className="font-nowey font-bold text-base tracking-tight text-white">
                No<span className="text-primary">Wey</span>
              </span>
            </div>
            <p className="text-xs text-zinc-500">
              CRM para redes de concessionárias
            </p>
          </div>

          {/* Nav links */}
          <nav aria-label="Links institucionais">
            <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2">
              {NAV_LINKS.map(({ label, href, external }) => (
                <li key={label}>
                  {external ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-zinc-500 hover:text-white transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    >
                      {label}
                    </a>
                  ) : (
                    <Link
                      href={href}
                      className="text-xs text-zinc-500 hover:text-white transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    >
                      {label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* WhatsApp icon */}
          <a
            href={WA_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10 text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
            aria-label="Contato comercial pelo WhatsApp"
          >
            <MessageCircle className="w-4 h-4" aria-hidden />
          </a>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-6 border-t border-white/5 text-center">
          <p className="text-xs text-zinc-600">
            © {CURRENT_YEAR} NoWey. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
}
